import { ChallengeType } from '@varo/graphql-schema-types';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useAcceptChallenge } from 'features/auth/hooks/useAcceptChallenge';
import { useSelectChallenge } from 'features/auth/resetPassword/hooks/useSelectChallenge/useSelectChallenge';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import { OtpSearchParams } from 'features/otp/constants/OtpSearchParams';
import { OtpVerificationType } from 'features/otp/constants/OtpVerificationType';
import useRedirect from 'features/otp/hooks/useRedirection/useRedirection';
import useResendOtp from 'features/otp/hooks/useResendOtp/useResendOtp';
import useVerifyChallenge from 'features/otp/hooks/useVerifyChallenge/useVerifyChallenge';
import OtpScreen from 'features/otp/OtpScreen';
import { PHONE_VERIFICATION_TYPE } from 'features/signup/constants';
import { useTranslation } from 'react-i18next';
import { BackHandler } from 'react-native';
import { isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useWhereToGo from 'shared/hooks/useWhereToGo/useWhereToGo';
import { useAuth } from 'shared/providers/auth';

const VERIFY_SCREEN_ERROR_CODES = [3, 9, 11];
const PENDING_CHALLENGE_STATE = 'PENDING';

const VerifyPhoneScreen = () => {
  const router = useRouter();
  const { t: translatedTextOtp } = useTranslation('otp');
  const { verifyChallenge, isLoading: isVerifyChallengeLoading } =
    useVerifyChallenge();
  const { acceptChallenge, isLoading: isAcceptChallengeLoading } =
    useAcceptChallenge();
  const {
    anonymousId,
    session,
    emailAddress,
    phoneNumber,
    screenType,
    lastFourDigitPhoneNumber,
    challengeId,
    identityId,
    flowKey,
    loginMethod,
    usernameType,
    deviceNotificationStatus,
  } = useLocalSearchParams<OtpSearchParams>();
  const { challengeRedirect } = useRedirect();
  const { initiateWhereToGo, isLoading: isWhereToGoFetching } = useWhereToGo();
  const { handleLogout } = useAuth();
  const { selectChallenge } = useSelectChallenge();
  const resendOtp = useResendOtp();

  const handleBackNavigation = async () => {
    if (flowKey === OtpFlowKey.SIGNUP) {
      if (isWeb) {
        // Web: Wipes history to prevent forward trackpad/arrow navigation
        router.replace('/signup');
      } else if (router.canGoBack()) {
        // Mobile: Plays native backward slide-right pop animation
        router.back();
      } else {
        // Fallback for deeplinks
        router.replace('/signup');
      }
      return true;
    }

    await handleLogout({
      shouldNavigateToLogin: true,
    });
    return true;
  };

  const signedIn = usePressableAnalytics({
    analyticsId: 'signedIn',
    customAttributes: {
      login_method: loginMethod,
      username_type: usernameType,
      device_notification_status: deviceNotificationStatus,
    },
  });

  useBackHandler(handleBackNavigation);

  // Strict hook for Android Physical Back Button
  useFocusEffect(() => {
    const onBackPress = () => {
      void handleBackNavigation();
      return true;
    };

    const subscription = BackHandler.addEventListener(
      'hardwareBackPress',
      onBackPress,
    );

    return () => {
      subscription.remove();
    };
  });

  const handleVerifyOtp = async (code: string) => {
    if (flowKey === OtpFlowKey.FORGOT_PASSWORD_1FA) {
      const result = await acceptChallenge({
        challengeId,
        identityId,
        email: emailAddress ?? '',
        otpCode: code,
      });

      if (result?.errorCode) {
        return {
          errorCode: result.errorCode,
          message: result.message,
          title: result.title,
        };
      }

      return {
        success: true,
        data: {
          challengeId,
          identityId,
          email: emailAddress,
        },
      };
    }
    const result = await verifyChallenge(
      {
        emailAddress: emailAddress ?? '',
        code: code,
        phoneNumber: phoneNumber ?? '',
        anonymousId: anonymousId ?? '',
        session: session ?? '',
      },
      { verifyType: PHONE_VERIFICATION_TYPE },
    );

    if (result.data?.challenge) {
      const { challenge, phoneLineNumber } = result.data || {};
      await challengeRedirect(
        challenge || '',
        emailAddress || '',
        phoneLineNumber || phoneNumber || '',
        phoneLineNumber || '',
        result.data?.session || '',
        screenType || '',
      );
    }

    if (result.success) {
      signedIn();
      initiateWhereToGo();
    }

    return {
      success: result.success,
      errorCode: result.errorCode,
      title: result.title,
      message: result.message,
      data: result.data,
    };
  };

  // Handler passed to OtpScreen for resend
  const handleResendOtp = async () => {
    if (flowKey === OtpFlowKey.FORGOT_PASSWORD_1FA) {
      const result = await selectChallenge({
        challengeId: challengeId ?? '',
        identityId: identityId ?? '',
        isCallForResendCode: true,
      });

      if (
        result?.challengeState === PENDING_CHALLENGE_STATE &&
        result?.challengeType === ChallengeType.Sms
      ) {
        return {};
      }

      return {
        errorCode: result?.errorCode,
        message: result?.message,
        title: result?.title,
      };
    }

    return await resendOtp({
      anonymousId: anonymousId ?? '',
      emailAddress: emailAddress ?? '',
      phoneNumber: phoneNumber ?? '',
    });
  };

  const description = `${translatedTextOtp('common.description')}${String(lastFourDigitPhoneNumber)}${translatedTextOtp('common.description1')}`;

  return (
    <OtpScreen
      flowKey={flowKey}
      verificationType={OtpVerificationType.PHONE}
      // Text content
      description={description}
      // External state
      isLoading={
        isVerifyChallengeLoading ||
        isAcceptChallengeLoading ||
        isWhereToGoFetching
      }
      // Configurable error codes
      screenLevelErrorCodes={VERIFY_SCREEN_ERROR_CODES}
      // Event handlers
      onVerifyOtp={handleVerifyOtp}
      onResendOtp={handleResendOtp}
      onBack={handleBackNavigation}
    />
  );
};

export default VerifyPhoneScreen;
